import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import Dots from '/@/components/dots';
import Toggle from '/@/components/forms/toggle';
import useUnitProvider from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useTableConstraintsFromAPI } from '/@/hooks/product-datasheet';
import { _ } from '/@/locale';
import {
    addProduct as addProductAction,
    removeProduct as removeProductAction,
    setProfileEnvironment as setProfileEnvironmentAction,
} from '/@/store/product-datasheet/actions';
import {
    getCropType,
    getProductDatasheetEnvironmentRangeForProfile,
    getSelectedIds,
} from '/@/store/product-datasheet/selectors';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

const Product = ({ product, brand, metric, onProductChange }) => {
    const dispatch = useDispatch();
    const [mergedProduct, setMergedProduct] = useState(product);
    const addProduct = bindActionCreators(addProductAction, dispatch);
    const removeProduct = bindActionCreators(removeProductAction, dispatch);
    const setProfileEnvironment = bindActionCreators(setProfileEnvironmentAction, dispatch);
    const units = useUnitProvider();
    const type = useSelector(getCropType);

    const { data: densityTableConstraints } = useTableConstraintsFromAPI({
        product: product.id,
        crop: type,
    });

    const environmentRanges = product.environmentRanges;

    const selectedIds = useSelector(getSelectedIds);
    const selectedEnvironmentRanges = useSelector(getProductDatasheetEnvironmentRangeForProfile);

    useEffect(() => {
        (async () => {
            setMergedProduct({
                ...product,
                profile: product,
                densityTableConstraints: densityTableConstraints || {},
                environmentRanges: environmentRanges || [],
            });
            onProductChange(mergedProduct);
        })();
    }, [densityTableConstraints, environmentRanges]);

    useEffect(() => {
        if (!mergedProduct.environmentRanges) return;
        if (mergedProduct.environmentRanges.length > 0) {
            const environment = mergedProduct.environmentRanges[0];
            const newEnvironment = { profileId: mergedProduct.id, rangeId: environment.referenceValue };
            setProfileEnvironment(newEnvironment);
        }
    }, [mergedProduct]);

    const getItemsAsOptions = (items) => {
        if (!items) return [];

        return items.map((item) => ({
            label: `${units.toYield(item.min, type)} - ${units.toYield(item.max, type)}`,
            value: item.referenceValue,
        }));
    };
    const options = getItemsAsOptions(mergedProduct.environmentRanges);

    const currentMetric = mergedProduct.metrics?.find(({ id }) => id == metric.id);
    const selected = selectedIds.includes(mergedProduct.id);
    const handleToggle = (value, id) => {
        value ? addProduct(id) : removeProduct(id);
    };
    const handleChange = (id) => {
        setProfileEnvironment({ profileId: mergedProduct.id, rangeId: id });
    };
    return (
        <div className={styles.root}>
            <div className={styles.toggle}>
                <Toggle
                    type='checkbox'
                    label={mergedProduct.catalogName} // MA-3393: Product datasheet mergedProduct name display (change for catalog name)
                    theme={brand.theme}
                    value={selected}
                    onChange={(value) => handleToggle(value, mergedProduct.id)}
                />
            </div>
            {currentMetric && (
                <div className={styles.dots}>
                    <Dots
                        max={brand.metricsMaxValue}
                        value={currentMetric.value}
                        showValues={brand.showMetricsValues}
                    />
                </div>
            )}
            {selected && options.length > 0 && (
                <div className={styles.select}>
                    <Select
                        appearance='outline'
                        size='sm'
                        value={selectedEnvironmentRanges[mergedProduct.id]}
                        options={options}
                        color={settings.app.theme}
                        onChange={(id) => handleChange(id)}
                        placeholder={_('Rangos de ambiente')}
                        clearable={false}
                    />
                </div>
            )}
        </div>
    );
};

export default Product;
